import React, {
    memo,
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import PropTypes from 'prop-types';
import 'chart.js/auto';
import { Line } from 'react-chartjs-2';
import { Icon, Loader, Text, TitleDivider, useTheme } from '@Sage/hoi-poi-ui';
import { alphaColor, getVar } from '@Sage/ssm-utils';
import moment from 'moment';

import { getLiteral } from 'utils/getLiteral';

import {
    MARKETING_CAMPAIGN_STATUS,
    normalizeMarketingCampaignStatusId,
} from '../../utils/marketingCampaignStatus';

import PerformanceOverTimeCustomRangePopover from './PerformanceOverTimeCustomRangePopover';
import { clampCustomRangeToSendAt, usePerformanceOverTime } from './usePerformanceOverTime';

function resolveChartJsInstanceFromForwardedRef(refLike) {
    if (!refLike) {
        return null;
    }
    if (typeof refLike.getChart === 'function') {
        return refLike.getChart() ?? null;
    }
    if (refLike.scales && refLike.canvas) {
        return refLike;
    }
    return null;
}

function readMarketingCampaignStatusRaw(campaign) {
    if (!campaign) {
        return undefined;
    }
    const raw = campaign.status;
    if (raw && typeof raw === 'object' && 'id' in raw) {
        return raw.id;
    }
    return raw;
}

const formatChartDate = (timestamp, granularity) => {
    if (!timestamp) return '';
    const date = moment.utc(timestamp).local();
    if (granularity === 'day') {
        return date.format('MMM D');
    }
    if (granularity === 'hour') {
        return date.format('MMM D, h:mm A');
    }
    if (granularity === 'week') {
        return date.format('MMM D');
    }
    if (granularity === 'month') {
        return date.format('MMM YYYY');
    }
    return date.format('L');
};

function pickSentTimestampIso(campaign) {
    if (!campaign) {
        return null;
    }

    const launched =
        typeof campaign.launchAt?.time === 'string' && campaign.launchAt.time.trim()
            ? campaign.launchAt.time
            : null;
    if (launched) {
        return launched;
    }

    const status = normalizeMarketingCampaignStatusId(readMarketingCampaignStatusRaw(campaign));
    if (status !== MARKETING_CAMPAIGN_STATUS.SENT && status !== MARKETING_CAMPAIGN_STATUS.SENDING) {
        return null;
    }
    const scheduled =
        typeof campaign.scheduleAt?.time === 'string' && campaign.scheduleAt.time.trim()
            ? campaign.scheduleAt.time
            : null;
    return scheduled;
}

/**
 * Lower bound for the custom analytics range picker: only once the campaign has actually sent / is sending.
 * Omitting planned `launchAt` for draft/scheduled avoids a future minDate disabling the whole calendar.
 * @returns {string|null}
 */
function pickCustomRangeMinSendIso(campaign) {
    if (!campaign) {
        return null;
    }
    const status = normalizeMarketingCampaignStatusId(readMarketingCampaignStatusRaw(campaign));
    if (status !== MARKETING_CAMPAIGN_STATUS.SENT && status !== MARKETING_CAMPAIGN_STATUS.SENDING) {
        return null;
    }
    const launched =
        typeof campaign.launchAt?.time === 'string' && campaign.launchAt.time.trim()
            ? campaign.launchAt.time
            : null;
    if (launched) {
        return launched;
    }
    const scheduled =
        typeof campaign.scheduleAt?.time === 'string' && campaign.scheduleAt.time.trim()
            ? campaign.scheduleAt.time
            : null;
    return scheduled;
}

/**
 * Sent marker only when the first series bucket is the send bucket (same granularity).
 * @returns {0|null}
 */
function resolveSentMarkerTickIndex(points, sentIso, granularity = 'day') {
    if (!sentIso || !Array.isArray(points) || points.length === 0) {
        return null;
    }

    const sentMs = bucketStartUtc(sentIso, granularity);
    const firstMs = bucketStartUtc(points[0]?.timestamp, granularity);
    if (Number.isNaN(sentMs) || Number.isNaN(firstMs) || firstMs !== sentMs) {
        return null;
    }

    return 0;
}

function bucketStartUtc(iso, granularity) {
    if (!iso) {
        return Number.NaN;
    }
    const m = moment.utc(iso);
    if (granularity === 'hour') {
        return m.clone().startOf('hour').valueOf();
    }
    if (granularity === 'month') {
        return m.clone().startOf('month').valueOf();
    }
    if (granularity === 'week') {
        return m.clone().startOf('isoWeek').valueOf();
    }
    return m.clone().startOf('day').valueOf();
}

/** Use fixed max + stepSize:1 while ceiling ≤ this (integer ticks without explosion). */
const Y_AXIS_INTEGER_STEP_MAX = 25;
const Y_AXIS_DEFAULT_MAX = 4;
/** Space between Y tick marks and the plot area (`ticks.padding`; toward the chart, not outward). */
const Y_AXIS_TICK_LABEL_PADDING = 16;

/** Drawable chart height — keep `.fm-performance-over-time__chart*` heights in SCSS in sync (same px). */
const CHART_VIEWPORT_HEIGHT_PX = 100;

/** Band above chartArea (`layout.padding.top`); datasets use `clip: false` so the stroke can use this without looking cut off. Keep modest — only visual headroom. */
const CHART_LAYOUT_PADDING_TOP_PX = 16;

const PerformanceOverTime = memo(({ campaignId, campaign, isActive }) => {
    const theme = useTheme();
    const customRangePopoverRef = useRef(null);

    const { applyCustomRange, customRange, data, isLoading, preset, setPreset, presets } =
        usePerformanceOverTime({
            campaignId,
            isActive,
        });

    const presetOptions = useMemo(
        () => [
            { name: presets.TODAY, label: getLiteral('label_today') },
            { name: presets.YESTERDAY, label: getLiteral('common_yesterday') },
            { name: presets.SEVEN_DAYS, label: '7D' },
            { name: presets.THIRTY_DAYS, label: '30D' },
            { name: presets.THREE_MONTHS, label: '3M' },
            { name: presets.SIX_MONTHS, label: '6M' },
            { name: presets.TWELVE_MONTHS, label: '12M' },
        ],
        [presets],
    );

    const handlePresetChipClick = useCallback(
        (name) => {
            customRangePopoverRef.current?.closeWithoutCommit();
            setPreset(name);
        },
        [setPreset],
    );

    const chartPalette = useMemo(
        () => ({
            opensStroke: getVar(theme.colors.semantic.positive400),
            opensFill: alphaColor(theme.colors.semantic.positive400, 0.22),
            clicksStroke: getVar(theme.colors.semantic.info400),
            clicksFill: alphaColor(theme.colors.semantic.info400, 0.22),
            tickColor: getVar(theme.colors.textLight.secondary),
            gridLineColor: alphaColor(theme.colors.grey[600], 0.22),
            sentMarkerBg: getVar(theme.colors.semantic.positive600),
            sentMarkerLine: getVar(theme.colors.semantic.positive500),
            sentMarkerIcon: '#ffffff',
            tooltipBackground: getVar(theme.colors.actionMinor[550]),
            tooltipBorder: alphaColor(theme.colors.primary.white, 0.18),
            tooltipMultiKeyBg: alphaColor(theme.colors.primary.white, 0.2),
            tooltipText: getVar(theme.colors.primary.white),
        }),
        [theme.colors],
    );

    const yAxisConfig = useMemo(() => {
        if (!data?.points?.length) {
            return { ceiling: Y_AXIS_DEFAULT_MAX, tightIntegerSteps: true };
        }
        let m = 0;
        for (const p of data.points) {
            const o = Number(p.uniqueOpens);
            const c = Number(p.uniqueClicks);
            m = Math.max(m, Number.isFinite(o) ? o : 0, Number.isFinite(c) ? c : 0);
        }
        const ceiling = Math.max(1, Math.ceil(m));
        return {
            ceiling,
            tightIntegerSteps: ceiling <= Y_AXIS_INTEGER_STEP_MAX,
        };
    }, [data]);

    const sentIso = useMemo(() => pickSentTimestampIso(campaign), [campaign]);

    const campaignSendAt = useMemo(() => {
        const iso = pickCustomRangeMinSendIso(campaign);
        if (!iso) {
            return null;
        }
        const parsed = new Date(iso);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
    }, [campaign]);

    const commitCustomRangeFromPopover = useCallback(
        (next) => applyCustomRange(clampCustomRangeToSendAt(next, campaignSendAt)),
        [applyCustomRange, campaignSendAt],
    );

    const sentMarkerTickIndex = useMemo(() => {
        if (!data?.points?.length || sentIso == null) {
            return null;
        }
        return resolveSentMarkerTickIndex(data.points, sentIso, data.granularity);
    }, [data, sentIso]);

    const [sentMarkerLayout, setSentMarkerLayout] = useState(null);
    const chartWrapRef = useRef(null);
    const chartRef = useRef(null);

    const syncSentMarkerLayout = useCallback(() => {
        const chart = resolveChartJsInstanceFromForwardedRef(chartRef.current);
        const wrap = chartWrapRef.current;

        if (!chart || sentMarkerTickIndex == null || !wrap) {
            setSentMarkerLayout(null);
            return;
        }

        const xScale = chart.scales?.x;
        const ca = chart.chartArea;
        const canvas = chart.canvas;

        if (!xScale || !canvas || !ca?.width) {
            setSentMarkerLayout(null);
            return;
        }

        const meta0 = chart.getDatasetMeta?.(0);
        const fromPoint = meta0?.data?.[sentMarkerTickIndex];
        const xFromPoint =
            fromPoint && typeof fromPoint.x === 'number' && Number.isFinite(fromPoint.x)
                ? fromPoint.x
                : null;

        const x = xFromPoint ?? xScale.getPixelForTick?.(sentMarkerTickIndex);
        if (!Number.isFinite(x)) {
            setSentMarkerLayout(null);
            return;
        }

        const wrapRect = wrap.getBoundingClientRect();
        const canvasRect = canvas.getBoundingClientRect();
        const canvasOffsetX = canvasRect.left - wrapRect.left;
        const canvasOffsetY = canvasRect.top - wrapRect.top;

        const leftPx = canvasOffsetX + x;
        const lineTopPx = canvasOffsetY + ca.top;
        const lineBottomPx = canvasOffsetY + ca.bottom;

        setSentMarkerLayout({
            leftPx,
            lineTopPx,
            lineHeightPx: Math.max(0, lineBottomPx - lineTopPx),
        });
    }, [sentMarkerTickIndex]);

    const syncSentMarkerLayoutRef = useRef(syncSentMarkerLayout);
    syncSentMarkerLayoutRef.current = syncSentMarkerLayout;

    const chartData = useMemo(() => {
        if (!data?.points?.length) {
            return { labels: [], datasets: [] };
        }

        const labels = data.points.map((point) =>
            formatChartDate(point.timestamp, data.granularity),
        );
        const opensData = data.points.map((point) => point.uniqueOpens);
        const clicksData = data.points.map((point) => point.uniqueClicks);

        return {
            labels,
            datasets: [
                {
                    label: getLiteral('label_marketing_campaigns_metrics_unique_opens'),
                    data: opensData,
                    borderColor: chartPalette.opensStroke,
                    backgroundColor: chartPalette.opensFill,
                    pointBackgroundColor: chartPalette.opensStroke,
                    pointBorderColor: chartPalette.opensStroke,
                    fill: true,
                    cubicInterpolationMode: 'monotone',
                    tension: 0,
                    pointRadius: 0,
                    pointHoverRadius: 3,
                    borderWidth: 1,
                    clip: false,
                },
                {
                    label: getLiteral('label_marketing_campaigns_metrics_unique_clicks'),
                    data: clicksData,
                    borderColor: chartPalette.clicksStroke,
                    backgroundColor: chartPalette.clicksFill,
                    pointBackgroundColor: chartPalette.clicksStroke,
                    pointBorderColor: chartPalette.clicksStroke,
                    fill: true,
                    cubicInterpolationMode: 'monotone',
                    tension: 0,
                    pointRadius: 0,
                    pointHoverRadius: 3,
                    borderWidth: 1,
                    clip: false,
                },
            ],
        };
    }, [chartPalette, data]);

    const chartOptions = useMemo(
        () => ({
            responsive: true,
            maintainAspectRatio: false,
            layout: {
                padding: {
                    top: CHART_LAYOUT_PADDING_TOP_PX,
                },
            },
            interaction: {
                mode: 'index',
                intersect: false,
            },
            plugins: {
                legend: {
                    display: false,
                },
                tooltip: {
                    backgroundColor: chartPalette.tooltipBackground,
                    titleColor: chartPalette.tooltipText,
                    bodyColor: chartPalette.tooltipText,
                    footerColor: chartPalette.tooltipText,
                    borderColor: chartPalette.tooltipBorder,
                    borderWidth: 1,
                    padding: 12,
                    displayColors: true,
                    usePointStyle: true,
                    multiKeyBackground: chartPalette.tooltipMultiKeyBg,
                    callbacks: {
                        labelTextColor: () => chartPalette.tooltipText,
                        label: (context) => {
                            const label = context.dataset.label || '';
                            const value = context.parsed.y || 0;
                            return `${label}: ${value}`;
                        },
                    },
                },
            },
            scales: {
                x: {
                    grid: {
                        display: false,
                    },
                    ticks: {
                        color: chartPalette.tickColor,
                        font: {
                            size: 11,
                        },
                        maxRotation: 0,
                        autoSkip: true,
                        maxTicksLimit: 8,
                    },
                },
                y: {
                    beginAtZero: true,
                    ...(yAxisConfig.tightIntegerSteps
                        ? { max: yAxisConfig.ceiling }
                        : { suggestedMax: yAxisConfig.ceiling }),
                    grid: {
                        display: true,
                        drawOnChartArea: true,
                        color: chartPalette.gridLineColor,
                        lineWidth: 1,
                        z: 1,
                    },
                    ticks: {
                        color: chartPalette.tickColor,
                        font: {
                            size: 11,
                        },
                        padding: Y_AXIS_TICK_LABEL_PADDING,
                        ...(yAxisConfig.tightIntegerSteps
                            ? { stepSize: 1 }
                            : {
                                  maxTicksLimit: 10,
                                  format: {
                                      maximumFractionDigits: 0,
                                  },
                              }),
                    },
                    border: {
                        display: false,
                    },
                },
            },
            animation: {
                onComplete: () => {
                    requestAnimationFrame(() => syncSentMarkerLayoutRef.current?.());
                },
            },
        }),
        [chartPalette, yAxisConfig],
    );

    useLayoutEffect(() => {
        if (!data?.points?.length || sentMarkerTickIndex == null || isLoading) {
            setSentMarkerLayout(null);
            return undefined;
        }
        const frame = requestAnimationFrame(() => syncSentMarkerLayoutRef.current?.());
        return () => cancelAnimationFrame(frame);
    }, [data?.points?.length, isLoading, preset, sentMarkerTickIndex, syncSentMarkerLayout]);

    useEffect(() => {
        const el = chartWrapRef.current;
        if (!el || typeof ResizeObserver === 'undefined') {
            return undefined;
        }
        const ro = new ResizeObserver(() => syncSentMarkerLayoutRef.current?.());
        ro.observe(el);
        return () => ro.disconnect();
    }, [syncSentMarkerLayout]);

    const legendItems = useMemo(
        () => [
            {
                key: 'opens',
                label: getLiteral('label_marketing_campaigns_metrics_unique_opens'),
                color: chartPalette.opensStroke,
            },
            {
                key: 'clicks',
                label: getLiteral('label_marketing_campaigns_metrics_unique_clicks'),
                color: chartPalette.clicksStroke,
            },
        ],
        [chartPalette],
    );

    if (!isActive) {
        return null;
    }

    return (
        <div className="fm-performance-over-time">
            <TitleDivider type="subtitle">
                {getLiteral('label_marketing_campaigns_metrics_performance_over_time')}
            </TitleDivider>
            <div className="fm-performance-over-time__header">
                <div className="fm-performance-over-time__preset-selector">
                    <PerformanceOverTimeCustomRangePopover
                        ref={customRangePopoverRef}
                        campaignSendAt={campaignSendAt ?? undefined}
                        customRange={customRange}
                        onApplyCustomRange={commitCustomRangeFromPopover}
                        preset={preset}
                        presets={presets}
                    />
                    {presetOptions.map((option) => {
                        const isSelected = option.name === preset;
                        return (
                            <button
                                key={option.name}
                                type="button"
                                className={
                                    isSelected
                                        ? 'fm-performance-over-time__preset-btn fm-performance-over-time__preset-btn--selected'
                                        : 'fm-performance-over-time__preset-btn'
                                }
                                onClick={() => handlePresetChipClick(option.name)}
                                aria-pressed={isSelected}
                            >
                                <Text
                                    type="badges"
                                    color={
                                        isSelected
                                            ? getVar(theme.colors.primary.white)
                                            : getVar(theme.colors.textLight.secondary)
                                    }
                                >
                                    {option.label}
                                </Text>
                            </button>
                        );
                    })}
                </div>
                <div className="fm-performance-over-time__legend">
                    {legendItems.map((item) => (
                        <div key={item.key} className="fm-performance-over-time__legend-item">
                            <span
                                className="fm-performance-over-time__legend-dot"
                                style={{ backgroundColor: item.color }}
                            />
                            <Text type="caption" color="neutral700">
                                {item.label}
                            </Text>
                        </div>
                    ))}
                </div>
            </div>

            <div className="fm-performance-over-time__chart-container">
                {isLoading ? (
                    <div className="fm-performance-over-time__loading">
                        <Loader size="big" />
                    </div>
                ) : data?.points?.length > 0 ? (
                    <div className="fm-performance-over-time__chart" ref={chartWrapRef}>
                        <Line
                            ref={chartRef}
                            data={chartData}
                            options={chartOptions}
                            height={CHART_VIEWPORT_HEIGHT_PX}
                        />
                        {sentMarkerLayout && sentMarkerTickIndex != null ? (
                            <>
                                <div
                                    className="fm-performance-over-time__sent-marker-line"
                                    style={{
                                        left: sentMarkerLayout.leftPx,
                                        top: sentMarkerLayout.lineTopPx,
                                        height: sentMarkerLayout.lineHeightPx,
                                        borderLeftColor: chartPalette.sentMarkerLine,
                                    }}
                                    aria-hidden
                                />
                                <div
                                    className="fm-performance-over-time__sent-marker-stack"
                                    style={{
                                        left: sentMarkerLayout.leftPx,
                                        top: Math.max(0, sentMarkerLayout.lineTopPx - 42),
                                    }}
                                >
                                    <div
                                        className="fm-performance-over-time__sent-marker-chip"
                                        style={{ backgroundColor: chartPalette.sentMarkerBg }}
                                    >
                                        <Icon
                                            name="emailSendOutline"
                                            color={chartPalette.sentMarkerIcon}
                                        />
                                        <Text
                                            type="caption"
                                            className="fm-performance-over-time__sent-marker-label"
                                            color={chartPalette.sentMarkerIcon}
                                        >
                                            {getLiteral(
                                                'label_marketing_campaigns_metrics_sent_plural',
                                            )}
                                        </Text>
                                    </div>
                                    <span
                                        className="fm-performance-over-time__sent-marker-caret"
                                        style={{
                                            borderTopColor: chartPalette.sentMarkerBg,
                                        }}
                                        aria-hidden
                                    />
                                </div>
                            </>
                        ) : null}
                    </div>
                ) : (
                    <div className="fm-performance-over-time__empty">
                        <Text type="body" color="neutral700">
                            {getLiteral('label_no_data_available')}
                        </Text>
                    </div>
                )}
            </div>
        </div>
    );
});

PerformanceOverTime.displayName = 'PerformanceOverTime';

PerformanceOverTime.propTypes = {
    campaign: PropTypes.object,
    campaignId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    isActive: PropTypes.bool,
};

export default PerformanceOverTime;
